import { z } from 'zod';

import type {
  Order,
  PostAdminV1StoresStoreIdOrdersIdRefundsBody,
} from '@nucleus-commerce/api-client/admin';
import { isMoney, type Money } from '@nucleus-commerce/core';

import { refundable } from './order-rules';

const moneyValue = z.custom<Money>(isMoney).nullable();
const whole = z
  .string()
  .trim()
  .refine((v) => /^\d+$/.test(v), { error: 'orders:refund.validation.wholeNumber' });

/**
 * The amount is capped at what was paid and not yet refunded, the way the backend caps it.
 * Line quantities are optional bookkeeping unless `restock` is on, when they decide what goes
 * back on hand.
 */
export function refundSchema(order: Pick<Order, 'paid' | 'refunded' | 'currency' | 'lines'>) {
  const max = refundable(order);
  const quantities = new Map(
    order.lines.map((line) => [line.id, line.quantity - line.cancelledQuantity]),
  );
  return z
    .object({
      amount: moneyValue,
      reason: z.string().trim().max(500),
      note: z.string().trim().max(2000),
      restock: z.boolean(),
      lines: z.array(z.object({ orderLineId: z.string(), quantity: whole })),
    })
    .superRefine((v, ctx) => {
      if (v.amount === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'orders:refund.validation.amountRequired',
        });
      } else if (v.amount.amount <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'orders:refund.validation.amountPositive',
        });
      } else if (v.amount.amount > max.amount) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'orders:refund.validation.overRefundable',
        });
      }
      v.lines.forEach((line, index) => {
        const cap = quantities.get(line.orderLineId) ?? 0;
        if (Number(line.quantity) > cap) {
          ctx.addIssue({
            code: 'custom',
            path: ['lines', index, 'quantity'],
            message: 'orders:refund.validation.overQuantity',
          });
        }
      });
      if (v.restock && !v.lines.some((line) => Number(line.quantity) > 0)) {
        ctx.addIssue({
          code: 'custom',
          path: ['restock'],
          message: 'orders:refund.validation.restockNeedsLines',
        });
      }
    });
}

export type RefundFormInput = z.input<ReturnType<typeof refundSchema>>;
export type RefundFormOutput = z.output<ReturnType<typeof refundSchema>>;

export function emptyRefundForm(
  order: Pick<Order, 'paid' | 'refunded' | 'currency' | 'lines'>,
): RefundFormInput {
  return {
    amount: refundable(order),
    reason: '',
    note: '',
    restock: false,
    lines: order.lines.map((line) => ({ orderLineId: line.id, quantity: '0' })),
  };
}

export function toRefundBody(
  values: RefundFormOutput,
): PostAdminV1StoresStoreIdOrdersIdRefundsBody {
  if (!values.amount) throw new Error('refund amount validated as present');
  return {
    amount: values.amount,
    reason: values.reason || null,
    note: values.note || null,
    restock: values.restock,
    lines: values.lines
      .map((line) => ({ orderLineId: line.orderLineId, quantity: Number(line.quantity) }))
      .filter((line) => line.quantity > 0),
  };
}
